import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    FormControlLabel,
    Switch,
    Menu,
    MenuItem
} from '@mui/material';
import { 
    Add, 
    Delete, 
    Edit, 
    Translate, 
    ArrowBack,
    CheckCircle,
    Warning
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import PageContainer from '../layout/PageContainer';

const AlgorithmicProblemContentManager = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [problem, setProblem] = useState(null);
    const [taskText, setTaskText] = useState('');
    const [solutions, setSolutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });
    
    // Dialog states
    const [taskDialog, setTaskDialog] = useState({ open: false });
    const [solutionDialog, setSolutionDialog] = useState({ open: false, solutionId: null, isEdit: false });
    const [newSolutionText, setNewSolutionText] = useState('');
    const [editSolutionText, setEditSolutionText] = useState('');
    
    // Menu states
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuSolutionId, setMenuSolutionId] = useState(null);

    const fetchProblem = useCallback(async () => {
        setLoading(true);
        try {
            const response = await userRequest.get(`/algorithmic-problem/${id}`);
            const problemData = response.data;
            setProblem(problemData);
            
            if (problemData.task) {
                setTaskText(problemData.task.text || '');
            } else {
                setTaskText('');
            }
            
            // Fetch solutions
            if (problemData.solutions) {
                setSolutions(problemData.solutions);
            } else {
                setSolutions([]);
            }
        } catch (error) {
            showAlert('Error loading problem: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchProblem();
        }
    }, [id, fetchProblem]);

    const showAlert = (message, severity = 'info') => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 5000);
    };

    const handleUpdateTask = async () => {
        if (!taskText.trim()) {
            showAlert('Task content cannot be empty', 'error');
            return;
        }

        try {
            await userRequest.patch(`/algorithmic-problem/${id}`, {
                taskContent: taskText.trim()
            });
            showAlert('Task updated successfully', 'success');
            setTaskDialog({ open: false });
            fetchProblem(); // Refresh to get updated task
        } catch (error) {
            showAlert('Error updating task: ' + error.message, 'error');
        }
    };

    const handleAddSolution = async () => {
        if (!newSolutionText.trim()) {
            showAlert('Solution content cannot be empty', 'error');
            return;
        }

        try {
            // Add solution through AlgorithmicProblem API
            await userRequest.post(`/algorithmic-problem/${id}/solutions`, {
                text: newSolutionText.trim()
            });

            showAlert('Solution added successfully', 'success');
            setNewSolutionText('');
            setSolutionDialog({ open: false });
            fetchProblem(); // Refresh to get updated solutions
        } catch (error) {
            showAlert('Error adding solution: ' + error.message, 'error');
        }
    };

    const handleEditSolution = async () => {
        if (!editSolutionText.trim()) {
            showAlert('Solution content cannot be empty', 'error');
            return;
        }

        try {
            // Update solution through AlgorithmicProblem API
            await userRequest.patch(`/algorithmic-problem/${id}/solutions/${solutionDialog.solutionId}`, {
                text: editSolutionText.trim()
            });
            showAlert('Solution updated successfully', 'success');
            setSolutionDialog({ open: false });
            fetchProblem(); // Refresh
        } catch (error) {
            showAlert('Error updating solution: ' + error.message, 'error');
        }
    };

    const handleDeleteSolution = async (solutionId) => {
        const confirmed = window.confirm(
            'Are you sure you want to delete this solution? This will also delete the associated text.'
        );
        
        if (!confirmed) return;

        try {
            // Delete solution through AlgorithmicProblem API
            await userRequest.delete(`/algorithmic-problem/${id}/solutions/${solutionId}`);

            showAlert('Solution deleted successfully', 'success');
            fetchProblem(); // Refresh
        } catch (error) {
            showAlert('Error deleting solution: ' + error.message, 'error');
        }
    };

    const handleTranslateTask = () => {
        if (problem?.task?._id) {
            navigate(`/text/translate/${problem.task._id}`);
        }
    };

    const handleTranslateSolution = (solutionId) => {
        if (solutionId) {
            navigate(`/text/translate/${solutionId}`);
        }
    };

    const handleOpenEditSolution = (solution) => {
        setEditSolutionText(solution.text);
        setSolutionDialog({ open: true, solutionId: solution.id, isEdit: true });
    };

    const handleMenuOpen = (event, solutionId) => {
        setAnchorEl(event.currentTarget);
        setMenuSolutionId(solutionId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuSolutionId(null);
    };

    const handleMenuDelete = () => {
        if (menuSolutionId) {
            handleDeleteSolution(menuSolutionId);
            handleMenuClose();
        }
    };

    const handleReadyToServeToggle = async (event) => {
        const newValue = event.target.checked;
        
        try {
            await userRequest.patch(`/algorithmic-problem/${id}`, {
                isReadyToServe: newValue
            });
            
            setProblem(prevProblem => ({
                ...prevProblem,
                isReadyToServe: newValue
            }));
            
            showAlert(
                newValue ? 'Problem published successfully' : 'Problem unpublished successfully', 
                'success'
            );
        } catch (error) {
            showAlert('Error updating publish status: ' + error.message, 'error');
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <Typography variant="h4">Loading...</Typography>
            </PageContainer>
        );
    }

    if (!problem) {
        return (
            <PageContainer>
                <Alert severity="error">Problem not found</Alert>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <IconButton onClick={() => navigate('/algorithmic-problems')}>
                    <ArrowBack />
                </IconButton>
                <Box flexGrow={1}>
                    <Typography variant="h4">{problem.title}</Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Chip 
                            label={problem.difficulty?.toUpperCase()} 
                            size="small" 
                            color={
                                problem.difficulty === 'easy' ? 'success' :
                                problem.difficulty === 'medium' ? 'warning' : 'error'
                            }
                        />
                        <Typography variant="body2" color="text.secondary">
                            {problem.language?.name}
                            {problem.level?.name && ` • ${problem.level.name}`}
                        </Typography>
                    </Box>
                </Box>
                <FormControlLabel
                    control={
                        <Switch
                            checked={problem.isReadyToServe || false}
                            onChange={handleReadyToServeToggle}
                            color="success"
                        />
                    }
                    label={
                        <Box display="flex" alignItems="center" gap={1}>
                            {problem.isReadyToServe ? (
                                <CheckCircle color="success" fontSize="small" />
                            ) : (
                                <Warning color="warning" fontSize="small" />
                            )}
                            <Typography variant="body2">
                                {problem.isReadyToServe ? 'Published' : 'Draft'}
                            </Typography>
                        </Box>
                    }
                />
            </Box>

            {alert.show && (
                <Alert severity={alert.severity} sx={{ mb: 2 }}>
                    {alert.message}
                </Alert>
            )}

            {/* Task Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">Task Description</Typography>
                    <Box display="flex" gap={1}>
                        <Button
                            variant="outlined"
                            startIcon={<Translate />}
                            onClick={handleTranslateTask}
                            disabled={!problem.task?._id}
                        >
                            Translate
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Edit />}
                            onClick={() => setTaskDialog({ open: true })}
                        >
                            Edit Task
                        </Button>
                    </Box>
                </Box>
                <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f9f9f9', minHeight: 100 }}>
                    {taskText ? (
                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{taskText}</Typography>
                    ) : (
                        <Typography color="text.secondary" fontStyle="italic">
                            No task description available
                        </Typography>
                    )}
                </Paper>
            </Paper>

            {/* Solutions Section */}
            <Paper sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">Solutions ({solutions.length})</Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setSolutionDialog({ open: true, solutionId: null, isEdit: false })}
                    >
                        Add Solution
                    </Button>
                </Box>

                {solutions.length > 0 ? (
                    <List>
                        {solutions.map((solution, index) => (
                            <ListItem
                                key={solution.id}
                                sx={{ border: 1, borderColor: 'divider', mb: 1, borderRadius: 1 }}
                            >
                                <ListItemText
                                    primary={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="subtitle1">
                                                Solution {index + 1}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Box sx={{ mt: 1 }}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    whiteSpace: 'pre-wrap',
                                                    maxHeight: 100,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {solution.text || 'No content'}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleTranslateSolution(solution.id)}
                                        sx={{ mr: 1 }}
                                        color="primary"
                                    >
                                        <Translate />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleOpenEditSolution(solution)}
                                        sx={{ mr: 1 }}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        onClick={(e) => handleMenuOpen(e, solution.id)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Box textAlign="center" py={4}>
                        <Typography variant="body1" color="text.secondary">
                            No solutions yet. Add your first solution to get started.
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Task Edit Dialog */}
            <Dialog open={taskDialog.open} onClose={() => setTaskDialog({ open: false })} maxWidth="md" fullWidth>
                <DialogTitle>Edit Task Description</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={12}
                        value={taskText}
                        onChange={(e) => setTaskText(e.target.value)}
                        sx={{ mt: 1 }}
                        placeholder="Enter task description..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTaskDialog({ open: false })}>Cancel</Button>
                    <Button onClick={handleUpdateTask} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Solution Add/Edit Dialog */}
            <Dialog 
                open={solutionDialog.open} 
                onClose={() => {
                    setSolutionDialog({ open: false, solutionId: null, isEdit: false });
                    setNewSolutionText('');
                    setEditSolutionText('');
                }} 
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle>
                    {solutionDialog.isEdit ? 'Edit Solution' : 'Add New Solution'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={12}
                        value={solutionDialog.isEdit ? editSolutionText : newSolutionText}
                        onChange={(e) => {
                            if (solutionDialog.isEdit) {
                                setEditSolutionText(e.target.value);
                            } else {
                                setNewSolutionText(e.target.value);
                            }
                        }}
                        sx={{ mt: 1 }}
                        placeholder="Enter solution explanation..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setSolutionDialog({ open: false, solutionId: null, isEdit: false });
                        setNewSolutionText('');
                        setEditSolutionText('');
                    }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={solutionDialog.isEdit ? handleEditSolution : handleAddSolution} 
                        variant="contained"
                    >
                        {solutionDialog.isEdit ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Menu for solution actions */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
                    <Delete sx={{ mr: 1 }} />
                    Delete Solution
                </MenuItem>
            </Menu>
        </PageContainer>
    );
};

export default AlgorithmicProblemContentManager;

